import { useMemo, useState } from 'react';
import { ManufacturingEquipment } from '../../types/manufacturingEquipment';
import EquipUseStatus from './EquipUseStatus';
import {
  compareEquipList,
  EquipListSortDir,
  EquipListSortKey,
} from './equipListSort';
import './InspectionCalibOverview.css';

type CalibFilter = 'ALL' | 'EXPIRED' | 'EXPIRING' | 'OK' | 'NONE';

interface Props {
  items: ManufacturingEquipment[];
  selectedId: number | null;
  onSelect: (equipmentId: number) => void;
  sortKey: EquipListSortKey;
  sortDir: EquipListSortDir;
  onToggleSort: (key: EquipListSortKey) => void;
  sortMark: (key: EquipListSortKey) => string;
  thClass: (key: EquipListSortKey) => string;
}

function statusLabel(status?: string) {
  switch (status) {
    case 'EXPIRED':
      return '만료';
    case 'EXPIRING':
      return '만료 임박';
    case 'OK':
      return '유효';
    default:
      return '미등록';
  }
}

function daysUntil(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function remainText(item: ManufacturingEquipment) {
  const days = daysUntil(item.latestCalibValidDate);
  if (days == null) return '-';
  if (days < 0) return `${Math.abs(days)}일 지남`;
  if (days === 0) return '오늘 만료';
  return `${days}일 남음`;
}

function InspectionCalibOverview({
  items,
  selectedId,
  onSelect,
  sortKey,
  sortDir,
  onToggleSort,
  sortMark,
  thClass,
}: Props) {
  const [filter, setFilter] = useState<CalibFilter>('ALL');

  const counts = {
    ALL: items.length,
    EXPIRED: items.filter((i) => i.calibStatus === 'EXPIRED').length,
    EXPIRING: items.filter((i) => i.calibStatus === 'EXPIRING').length,
    OK: items.filter((i) => i.calibStatus === 'OK').length,
    NONE: items.filter((i) => !i.calibStatus || i.calibStatus === 'NONE').length,
  };

  const rows = useMemo(() => {
    const filtered = items.filter((item) =>
      filter === 'ALL' ? true : (item.calibStatus || 'NONE') === filter
    );
    return filtered
      .slice()
      .sort((a, b) => compareEquipList(a, b, sortKey, sortDir));
  }, [items, filter, sortKey, sortDir]);

  return (
    <div className="insp-calib-overview">
      <div className="insp-calib-overview__summary">
        {(
          [
            ['ALL', '전체'],
            ['EXPIRED', '만료'],
            ['EXPIRING', '만료 임박'],
            ['OK', '유효'],
            ['NONE', '미등록'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`insp-calib-overview__chip is-${key.toLowerCase()}${
              filter === key ? ' is-active' : ''
            }`}
            onClick={() => setFilter(key)}
          >
            <strong>{counts[key]}</strong>
            <span>{label}</span>
          </button>
        ))}
      </div>

      <p className="insp-calib-overview__hint">
        기본은 설비번호 내림차순입니다. 순서·설비번호 헤더를 클릭하면 정렬이 바뀝니다. 만료
        임박은 설비별 설정 일수(기본 30일) 이내입니다.
      </p>

      <table className="ec-table contract-panel__table insp-calib-overview__table">
        <thead>
          <tr>
            <th style={{ width: '100px' }}>교정 상태</th>
            <th
              style={{ width: '64px' }}
              className={thClass('sortOrder')}
              onClick={() => onToggleSort('sortOrder')}
            >
              순서{sortMark('sortOrder')}
            </th>
            <th
              style={{ width: '120px' }}
              className={thClass('equipmentCode')}
              onClick={() => onToggleSort('equipmentCode')}
            >
              설비번호{sortMark('equipmentCode')}
            </th>
            <th>설비명</th>
            <th style={{ width: '70px' }}>구분</th>
            <th style={{ width: '110px' }}>유효 일자</th>
            <th style={{ width: '100px' }}>잔여/경과</th>
            <th style={{ width: '90px' }}>주기</th>
            <th style={{ width: '90px' }}>임박기준</th>
            <th style={{ width: '90px' }}>담당자</th>
            <th style={{ width: '80px' }}>사용</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={11} style={{ textAlign: 'center', color: '#777' }}>
                해당 조건의 설비가 없습니다.
              </td>
            </tr>
          ) : (
            rows.map((item) => {
              const status = item.calibStatus || 'NONE';
              return (
                <tr
                  key={item.equipmentId}
                  className={`${selectedId === item.equipmentId ? 'is-selected' : ''} ${
                    status === 'EXPIRED' || status === 'EXPIRING' ? 'is-urgent' : ''
                  }`}
                  onClick={() => onSelect(item.equipmentId)}
                >
                  <td>
                    <span className={`equip-calib-status is-${status.toLowerCase()}`}>
                      {statusLabel(status)}
                    </span>
                  </td>
                  <td>{(item.sortOrder ?? 0) + 1}</td>
                  <td>{item.equipmentCode || '-'}</td>
                  <td>{item.equipmentName}</td>
                  <td>{item.ownershipType === 'RENTAL' ? '대여' : '자체'}</td>
                  <td>{item.latestCalibValidDate || '-'}</td>
                  <td>{remainText(item)}</td>
                  <td>{item.calibCycleMonths ? `${item.calibCycleMonths}개월` : '-'}</td>
                  <td>{item.calibWarnDays != null ? `${item.calibWarnDays}일` : '30일'}</td>
                  <td>{item.managerName || '-'}</td>
                  <td>
                    <EquipUseStatus useYn={item.useYn} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default InspectionCalibOverview;
